/**
 * instant-cart-drawer.js
 *
 * Opens the cart drawer the moment Add to Cart is clicked, with a spinner,
 * instead of waiting for the add requests to finish.
 *
 * Why: Kaching Bundles adds a bundle in two sequential requests (gift line,
 * then the main line with the selling plan). Measured on this store that is
 * about 1.8s each, so the drawer only appeared after ~4s. Nothing here makes
 * the requests faster; it removes the dead wait before any feedback shows.
 *
 * This never calls preventDefault, so Kaching and the theme product form keep
 * full ownership of the add itself. It only opens the drawer early and clears
 * its own loading class once real cart content lands.
 */
(function () {
  'use strict';

  var LOADING = 'hf-instant-loading';
  var MAX_WAIT = 8000; // hard stop, so a failed add can never leave a stuck spinner
  var MIN_SHOW = 300; // avoids a spinner flash when the cart answers quickly

  var CSS =
    // spinner, centred in the panel, on top of whatever the drawer currently holds
    'cart-drawer.' + LOADING + ' .drawer__inner{position:relative}' +
    'cart-drawer.' + LOADING + ' .drawer__inner::after{' +
    'content:"";position:absolute;top:50%;left:50%;width:3.2rem;height:3.2rem;' +
    'margin:-1.6rem 0 0 -1.6rem;border:2px solid rgba(var(--color-foreground),0.18);' +
    'border-top-color:rgb(var(--color-foreground));border-radius:50%;' +
    'animation:hf-instant-spin 0.7s linear infinite;z-index:3;pointer-events:none}' +
    '@keyframes hf-instant-spin{to{transform:rotate(360deg)}}' +
    // first add: never flash "your cart is empty" while the add is still running
    'cart-drawer.' + LOADING + '.is-empty .drawer__inner-empty{display:none!important}' +
    'cart-drawer.' + LOADING + '.is-empty .drawer__inner>*:not(.drawer__header){visibility:hidden}' +
    // the empty state switches .drawer__inner to a centred grid, which drops the
    // heading into the middle of the panel. Put it back to the normal column.
    'cart-drawer.' + LOADING + '.is-empty .drawer__inner{' +
    'display:flex;flex-direction:column;align-items:stretch;justify-content:flex-start}' +
    // adding to a cart that already has lines: keep the lines, just dim them
    'cart-drawer.' + LOADING + ':not(.is-empty) .cart-drawer__body{opacity:0.4;pointer-events:none}';

  var state = { active: false, since: 0, timer: null, observer: null };

  function injectStyle() {
    if (document.getElementById('hf-instant-cart-drawer-style')) return;
    var style = document.createElement('style');
    style.id = 'hf-instant-cart-drawer-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function lineCount(drawer) {
    return drawer.querySelectorAll('.cart-item, [id^="CartDrawer-Item"]').length;
  }

  function hasError(drawer) {
    var box = drawer.querySelector('#CartDrawer-CartErrors');
    return !!(box && box.textContent && box.textContent.trim());
  }

  function stop(drawer) {
    if (!state.active) return;
    state.active = false;
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    if (state.observer) { state.observer.disconnect(); state.observer = null; }
    (drawer || drawerEl() || { classList: { remove: function () {} } }).classList.remove(LOADING);
  }

  function finish(drawer) {
    var waited = Date.now() - state.since;
    if (waited < MIN_SHOW) {
      setTimeout(function () { stop(drawer); }, MIN_SHOW - waited);
      return;
    }
    stop(drawer);
  }

  function isAddToCartClick(target) {
    if (!target || !target.closest) return false;
    // the drawer's own controls, quantity steppers and express checkout are not adds
    if (target.closest('cart-drawer')) return false;
    if (target.closest('quantity-input')) return false;
    if (target.closest('.shopify-payment-button')) return false;

    var btn = target.closest('button, input[type="submit"], .atc-button');
    if (!btn || btn.disabled) return false;
    if (btn.getAttribute('name') === 'checkout') return false;
    if (btn.getAttribute('name') === 'plus' || btn.getAttribute('name') === 'minus') return false;

    var cls = (btn.className || '').toString();
    if (btn.classList.contains('atc-button')) return true;
    if (/kaching-bundles__cta|ofs__atc/.test(cls)) return true;
    // any real submit inside a theme add-to-cart form
    return btn.type === 'submit' && !!btn.closest('form[action*="/cart/add"]');
  }

  function start(drawer) {
    if (state.active) return;
    state.active = true;
    state.since = Date.now();
    injectStyle();
    drawer.classList.add(LOADING);

    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      // custom element not upgraded yet, fall back to the classes open() sets
      drawer.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    }

    state.observer = new MutationObserver(function () {
      if (!state.active) return;
      // the drawer was closed again, or the add failed loudly
      if (!drawer.classList.contains('active') || hasError(drawer)) { stop(drawer); return; }
      if (lineCount(drawer) > 0) finish(drawer);
    });
    state.observer.observe(drawer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    state.timer = setTimeout(function () { stop(drawer); }, MAX_WAIT);
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!isAddToCartClick(event.target)) return;
      var drawer = drawerEl();
      if (!drawer) return; // e.g. a template that renders no drawer
      start(drawer);
    },
    true // capture, so the drawer opens before the app handlers run
  );

  window.addEventListener('pagehide', function () { stop(null); });
})();
